import $ from "jquery";
import _ from "lodash";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ComposedChart, LabelList, Legend, Line, Pie, PieChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Divider, Header, Input, Label, Message, Segment, Select } from "semantic-ui-react"
import { useRequest } from "../hooks/commonHooks"
import "../assets/scss/components/DashboardComponent.scss"
import moment from "moment";
import { useHistory } from "react-router-dom";
import { PlotChannelColors } from "../models/common_model";
import { useCurrentUser } from "../hooks/auth-hook";
import { nextTick } from "process";

interface ProductGeneral {
    result_id: number;
    product: string;
    order: string;
    testing_station: string;
    pass: number;
    total: number;
}

interface ProductDataRow { name: string, total: number, pass: number, fail: number, ratio: number, realRatio: number, resultId?: number }

const passColor = PlotChannelColors[0];
const failColor = '#eb5a46';
const ratioColor = 'orange';

export function DashboardComponent(props: {}) {
    const [height, setHeight] = useState('auto');
    const [filters, setFilters] = useState({ last_unit: 'months', last_value: 3 });
    const [generals, loading, setGeneralParameters] = useRequest<ProductGeneral[]>('testings/dashboard/general', { last_date: createDate(filters.last_unit, filters.last_value) }, 'post');
    const [productData, setProductData] = useState<ProductDataRow[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<string>();
    const [detailData, setDetailData] = useState<ProductDataRow[]>([]);
    const currentUser = useCurrentUser();

    useEffect(() => {
        setGeneralParameters({ last_date: createDate(filters.last_unit, filters.last_value) });
    }, [filters.last_unit, filters.last_value, setGeneralParameters]);


    useEffect(() => {
        const productCount: { [product: string]: ProductDataRow } = {} as any;
        if (generals) {
            for (const d of generals) {
                const product = d.product.trim()
                if (!productCount[product]) {
                    productCount[product] = { name: product, total: 0, pass: 0, fail: 0, ratio: 0, realRatio: 0 }
                }
                productCount[product].total += d.total;
                productCount[product].pass += d.pass;
                productCount[product].fail -= (d.total - d.pass);
            }
        }

        const productDataList = Object.keys(productCount).map(name => productCount[name]);
        const maxPass = _.max(productDataList.map(d => d.pass))!
        const currentProductData = productDataList.map(d => ({ ...d, ratio: d.pass * maxPass / d.total, realRatio: d.pass / d.total }));
        setProductData(currentProductData);

        if (productDataList.length > 0) {
            setSelectedProduct(p => p && productDataList.find(ep => ep.name === p) ? p : productDataList[0].name)
        }
    }, [generals]);

    useEffect(() => {
        if (generals && selectedProduct) {
            const detailCount = {} as any;
            for (const d of generals) {
                if (d.product.trim() === selectedProduct) {
                    const name = d.order + ' / ' + d.testing_station;
                    const ratio = d.pass / d.total;
                    if (!detailCount[name]) {
                        detailCount[name] = { name, total: 0, pass: 0, fail: 0, ratio: 0, realRatio: 0, resultId: d.result_id }
                    }
                    detailCount[name].total += d.total;
                    detailCount[name].pass += d.pass;
                    detailCount[name].fail -= (d.total - d.pass);
                }
            }

            const detailDataList = Object.keys(detailCount).map(name => detailCount[name]);
            const maxPass = _.max(detailDataList.map(d => d.pass))!

            setDetailData(detailDataList.map(d => ({ ...d, ratio: d.pass * maxPass / d.total, realRatio: d.pass / d.total })));
        } else {
            setDetailData([]);
        }

    }, [generals, selectedProduct]);

    useLayoutEffect(() => {
        const onResize = () => {
            setHeight(window.innerHeight - ($('#dashboardRoot').offset()?.top || 0) - 10 + 'px');
        }

        $(window).on('resize', onResize);

        onResize();

        return () => {
            $(window).off('resize', onResize);
        };
    }, []);

    return <Segment loading={loading} style={{ height }} className="dashboard" id="dashboardRoot">
        <Input labelPosition='right' size="mini" action>
            <Label basic>Last</Label>
            <input style={{ width: '5rem' }} type="number" pattern="[0-9]+" value={filters.last_value} onChange={e => {
                setFilters(vs => ({ ...vs, last_value: parseInt(e.target.value) || 1 }))
            }} />

            <Select compact value={filters.last_unit} onChange={(e, d) => {
                setFilters(vs => ({ ...vs, last_unit: d.value as string }))
            }} options={['days', 'weeks', 'months', 'years'].map(v => ({ key: v, text: v, value: v }))}></Select>
        </Input>

        <Divider />
        {productData.length > 0 ? <>
            <Header>All Products</Header>
            <ResponsiveContainer width="100%" height="33%">
                <ComposedChart
                    data={productData}
                    margin={{
                        top: 15,
                        right: 30,
                        left: 20,
                        bottom: 5,
                    }}
                    stackOffset="sign"
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <ReferenceLine y={0} />
                    <XAxis dataKey="name" tick={tickProps => {
                        const { x, y, payload } = tickProps;
                        const { value } = payload;

                        return <text className={`selectable ${selectedProduct === value ? 'selected' : ''}`} x={x} y={y + 10} textAnchor="middle" onClick={e => {
                            e.preventDefault();
                            setSelectedProduct(value);
                        }}>{`${value}`}</text>;
                    }} />
                    <YAxis />
                    <Tooltip formatter={(d, n, item, index, payload) => {
                        if (n === 'ratio') {
                            return [(payload[2].payload.realRatio * 100).toFixed(1) + '%', 'Pass Percent']
                        } else {
                            return [Math.abs(parseInt(d as string)), _.capitalize(n as string) + ' Serials']
                        }
                    }} />
                    <Legend align="left" formatter={(v: string) => ({ 'pass': 'Pass Serials', 'fail': 'Fail Serials', ratio: 'Pass Percent' }[v])} />
                    <Bar type="monotone" dataKey="pass" fill={passColor} stackId="1" />
                    <Bar type="monotone" dataKey="fail" fill={failColor} stackId="1" />
                    <Line type="monotone" dataKey="ratio" stroke={ratioColor} isAnimationActive={false}>
                        <LabelList content={PassPercentLabel(productData.map(p => p.realRatio))} />
                    </Line>

                </ComposedChart>

            </ResponsiveContainer>
        </> : <Message size="big">No Data</Message>}

        {detailData.length > 0 && <>
            <Divider />

            <Header>Details of {selectedProduct}</Header>
            <ResponsiveContainer width="100%" height="33%">
                <ComposedChart
                    data={detailData}
                    margin={{
                        top: 15,
                        right: 30,
                        left: 20,
                        bottom: 5,
                    }}
                    stackOffset="sign"
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <ReferenceLine y={0} />
                    <XAxis dataKey="name" tick={tickProps => {
                        const { x, y, payload } = tickProps;
                        const { value } = payload;

                        return <text className={currentUser.permissions.testing.view ? 'selectable' : ''} x={x} y={y + 10} textAnchor="middle" onClick={e => {
                            e.preventDefault();
                            if (currentUser.permissions.testing.view) {
                                const detail = detailData.find(d => d.name === value)
                                if (detail) {
                                    window.open(`/testing_visual/${detail.resultId}/overview?start_time=${createDate(filters.last_unit, filters.last_value)} 00:00`, `testing_visual_detail_${detail.resultId}`)
                                }
                            }
                        }}>{`${value}`}</text>;
                    }} />
                    <YAxis />
                    <Tooltip formatter={(d, n, item, index, payload) => {
                        if (n === 'ratio') {
                            return [(payload[2].payload.realRatio * 100).toFixed(1) + '%', 'Pass Percent']
                        } else {
                            return [Math.abs(parseInt(d as string)), _.capitalize(n as string) + ' Serials']
                        }
                    }} />
                    <Legend align="left" formatter={(v: string) => ({ 'pass': 'Pass Serials', 'fail': 'Fail Serials', ratio: 'Pass Percent' }[v])} />
                    <Bar type="monotone" dataKey="pass" fill={passColor} stackId="1" />
                    <Bar type="monotone" dataKey="fail" fill={failColor} stackId="1" />
                    <Line type="monotone" dataKey="ratio" stroke={ratioColor} isAnimationActive={false}>
                        <LabelList content={PassPercentLabel(detailData.map(p => p.realRatio))}/>
                    </Line>

                </ComposedChart>

            </ResponsiveContainer>
        </>}

    </Segment>
}

function createDate(unit: string, value: number) {
    return moment().subtract(unit, value as any).format('YYYY-MM-DD');
}


function PassPercentLabel(values: number[]) {
    return function (props: any) {
        const { x, y, index } = props;

        return (
            <text x={x} y={y} dy={-5} textAnchor="middle" fill={ratioColor}>
                {(values[index] * 100).toFixed(1) + '%'}
            </text>

        );
    }
}